import React, { useState, useEffect } from 'react';
import { generateColorFromId, formatLabel, sanitizeModeName, convertSlotsToBlocks } from '../../utils/modeScheduleUtils';

function DayScheduleTimeline({ modes, day, schedules, date, isToday, onCreateBlock, onEditBlock }) {
    const blocksData = convertSlotsToBlocks(schedules?.slots || {}, modes);
    const visibleBlocks = blocksData.filter(b => b.label !== 'idle');

    const [isDragging, setIsDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(null);
    const [dragEndX, setDragEndX] = useState(null);

    const [currentTime, setCurrentTime] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentTime(new Date());
        }, 30000);

        return () => clearInterval(interval);
    }, []);

    const PIXELS_PER_HOUR = 64;
    const pxToHours = px => px / PIXELS_PER_HOUR;
    const EPSILON = 0.00001;

    const snapToQuarterHour = hourFloat => {
        const quarter = 0.25;
        return Math.round(hourFloat / quarter) * quarter;
    };

    const toTimeString = hourFloat => {
        const hour = Math.floor(hourFloat);
        const minutes = Math.round((hourFloat - hour) * 60);
        return `${String(hour).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
    };

    const handleMouseDown = e => {
        if (e.button === 0) {
            const trackRect = e.currentTarget.getBoundingClientRect();
            const x = e.pageX - trackRect.left + e.currentTarget.scrollLeft;
            setIsDragging(true);
            setDragStartX(x);
            setDragEndX(x);
        }
    };

    const handleMouseMove = e => {
        if (isDragging) {
            const trackRect = e.currentTarget.getBoundingClientRect();
            const x = e.pageX - trackRect.left + e.currentTarget.scrollLeft;
            setDragEndX(x);
        }
    };

    const handleMouseUp = () => {
        if (isDragging) {
            setIsDragging(false);
            const startX = Math.min(dragStartX, dragEndX);
            const endX = Math.max(dragStartX, dragEndX);

            let startHour = snapToQuarterHour(pxToHours(startX));
            let endHour = snapToQuarterHour(pxToHours(endX));
            const durationHours = endHour - startHour;

            const direction = dragStartX < dragEndX ? 'right' : 'left';
            const overlapping = visibleBlocks.filter(b => !(endHour <= b.start + EPSILON || startHour >= b.end - EPSILON));

            if (overlapping.length > 0) {
                if (direction === 'right') {
                    const limit = Math.min(...overlapping.map(b => b.start));
                    endHour = Math.min(endHour, limit);
                } else {
                    const limit = Math.max(...overlapping.map(b => b.end));
                    startHour = Math.max(startHour, limit);
                }
            }

            if (endHour > startHour + EPSILON) {
                const newBlock = {
                    id: `${date}-${Date.now()}`,
                    start: startHour,
                    end: endHour,
                    duration: {
                        hours: Math.floor(durationHours),
                        minutes: Math.round((durationHours % 1) * 60),
                    },
                    date: date,
                };
                onCreateBlock && onCreateBlock(newBlock);
            }
        }
    };

    let dragSelectionStyle = {};
    if (isDragging && dragStartX !== null && dragEndX !== null) {
        const left = Math.min(dragStartX, dragEndX);
        const width = Math.abs(dragEndX - dragStartX);
        dragSelectionStyle = {
            position: 'absolute',
            top: '8px',
            height: '48px',
            left: `${left}px`,
            width: `${width}px`,
            backgroundColor: 'rgba(59, 130, 246, 0.3)',
            pointerEvents: 'none',
            zIndex: 3,
        };
    }

    const currentHour = currentTime.getUTCHours() + currentTime.getUTCMinutes() / 60;

    return (
        <div className="timeline">
            <div className="timeline__header">{day}</div>
            <div className="timeline__track" onMouseDown={handleMouseDown} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
                <div className="timeline__hours">
                    {[...Array(25)].map((_, hour) => (
                        <div key={hour} className="timeline__hour">
                            {hour}:00
                        </div>
                    ))}
                </div>
                <div className="timeline__lines-container">
                    {[...Array(24 * 4 + 1)].map((_, i) => {
                        const leftPos = i * (PIXELS_PER_HOUR * 0.25);
                        return <div key={i} className="timeline__subdivision" style={{ left: `${leftPos}px` }} />;
                    })}
                </div>
                <div className="timeline__blocks">
                    {visibleBlocks.map((block, index) => {
                        const width = (block.end - block.start) * PIXELS_PER_HOUR;
                        const left = block.start * PIXELS_PER_HOUR;
                        return (
                            <div
                                key={index}
                                className="timeline__block"
                                style={{
                                    width: `${width}px`,
                                    left: `${left}px`,
                                    backgroundColor: generateColorFromId(sanitizeModeName(block.label)),
                                }}
                                onClick={() => onEditBlock && onEditBlock(block, date)}
                                title={`${toTimeString(block.start)} - ${toTimeString(block.end)}`}
                            >
                                {formatLabel(block.label)}
                            </div>
                        );
                    })}
                    {isToday && (
                        <div className="timeline__marker" style={{ left: `${currentHour * PIXELS_PER_HOUR}px` }}>
                            <div className="timeline__marker-label">Active</div>
                        </div>
                    )}
                </div>
                {isDragging && <div style={dragSelectionStyle}></div>}
            </div>
        </div>
    );
}

export default DayScheduleTimeline;
